import { css } from '@emotion/react'
import { fetchAboutUsPageContent } from 'api/contentful/aboutUsPage'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import { AboutHero } from 'components/content/about/aboutHero/AboutHero'
import { AboutTeam } from 'components/content/about/aboutTeam/AboutTeam'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { GroupLifeNavigation } from 'components/groupLifeSection/GroupLifeNavigation'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { useGroupLifeEnabled } from 'logic/subscription/plan/groupLife/useGroupLifeEnabled'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { IAboutUsPageFields } from 'types/generated/contentful'
import { REVALIDATE_SECS } from 'utils/constants'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { BREAKPOINTS } from 'utils/styled'

interface AboutUsPageStaticProps {
  content: IAboutUsPageFields
  faq: ContentfulFAQ
}

export default function Page(props: AboutUsPageStaticProps) {
  const { t } = useTranslation('group_life')
  useGroupLifeEnabled({ enableOnReady: true })

  return (
    <Layout header={<GroupLifeNavigation />} footer={<Footer variant="full" />}>
      <PageTitle title={t('group_life.about_page.meta.title')} />
      <PageMeta
        description={t('group_life.about_page.meta.description')}
        image="/images/preview/about-group-life.png"
      />

      <AboutHero
        content={{
          aboutHeroCaption: t('group_life.about_page.hero_caption'),
          aboutHeroTitle: t('group_life.about_page.title'),
          aboutHeroDescription: t('group_life.about_page.description'),
        }}
      />
      <AboutTeam content={{ ...props.content }} />
      <FaqTwoColumns
        content={props.faq}
        css={css`
          padding: 2.25rem 0 8.75rem;
          @media ${BREAKPOINTS.MD.max} {
            padding: 2rem 0 3rem;
          }
        `}
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const content = await fetchAboutUsPageContent()
  const faq = await getContentfulFAQ('about-us')
  if (!content) throw Error('Could not fetch About Us page content.')

  const props: AboutUsPageStaticProps = {
    content,
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['group_life', 'security'])),
    faq,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
